// Single source of truth for the "Is Home Approach free?" disclosures.
// Consumed by the explainer page (/how-we-make-money), the homepage trust
// block under "Get paid to get ready.", and the matching disclosure
// accordion on /experts so the three surfaces can never drift.

export const EXPLAINER_PATH = "/how-we-make-money";

export type TrustPoint = {
  id: string;
  title: string;
  detail: string;
};

/** The compact 4-point block used on the homepage. */
export const TRUST_POINTS: TrustPoint[] = [
  {
    id: "free",
    title: "Free to use",
    detail:
      "Every tool, guide, score check, and Homie conversation is free. No subscription, no card, no trial that turns into a bill.",
  },
  {
    id: "funded",
    title: "Rewards funded by Home Approach",
    detail:
      "Approach Points, cashback, and closing-day rewards are paid out of our own pocket — not passed through from a lender or sponsor.",
  },
  {
    id: "fees",
    title: "Paid by employers, never by providers",
    detail:
      "Employers who offer Home Approach as a benefit are how we're paid. No agent, lender, title company, or other provider pays us — not to list them, rank them, or when you close.",
  },
  {
    id: "choice",
    title: "No obligation to pick a partner",
    detail:
      "A match is a recommendation, not a requirement. Bring your own lender or agent and you keep every tool and every reward.",
  },
];

export type ExplainerSection = {
  id: string;
  heading: string;
  jumpLabel: string;
  body: string[];
};

export const EXPLAINER_SECTIONS: ExplainerSection[] = [
  {
    id: "rewards",
    heading: "What you pay: nothing",
    jumpLabel: "What you pay",
    body: [
      "Home Approach is free for buyers, renters, and homeowners. Creating an account, checking your credit score, running the Stack, using every calculator, reading every guide, and talking to Homie all cost zero dollars. There is no paid tier you get nudged into and no card on file.",
      "We are not a lender, a brokerage, or a credit repair company. We do not charge you an application fee, a success fee, or a percentage of anything you save.",
    ],
  },
  {
    id: "funding",
    heading: "Who funds the rewards",
    jumpLabel: "Rewards",
    body: [
      "Approach Points convert to real cash, and the credit-score bonus and closing-day cashback are real payouts. All of it is funded directly out of pocket by Home Approach.",
      "No lender, agent, or partner pays you on our behalf, and no reward is contingent on you choosing a particular company. We fund rewards because members who finish the boring preparation steps become homeowners, and homeowners are the business.",
    ],
  },
  {
    id: "fees",
    heading: "Who pays us: employers",
    jumpLabel: "Who pays us",
    body: [
      "Home Approach is a benefit employers offer their people, and that's our business model. Employers who sponsor Home Approach pay us. Community organizations can partner with us for free.",
      "We don't take commissions, referral fees, or payments from real estate agents, lenders, title companies, Hero Benefits Alliance, or any other provider. Not to list them, not to rank them, and not when you close.",
      "Employers see anonymous totals only — never your name, credit, or plans.",
    ],
  },
  {
    id: "choice",
    heading: "You pick the partner",
    jumpLabel: "Choice",
    body: [
      "A match is a recommendation. You are never required to use anyone we suggest, and declining costs you nothing.",
      "Members regularly bring their own lender, their own agent, or a professional a family member recommended — and keep full access to every tool, milestone, and reward. If you would rather compare on your own first, the lender quote comparison and readiness tools are built for exactly that.",
    ],
  },
  {
    id: "homie",
    heading: "Homie vs. Homie.com",
    jumpLabel: "Homie",
    body: [
      "Homie is the name of Home Approach's AI guide — the character who walks you through your score, your timeline, and your milestones inside this product.",
      "Homie is not affiliated with Homie.com or with any similarly named brokerage, mortgage company, or app. If you were looking for that company, this is not it.",
    ],
  },
];

export type ExpertRole = {
  id: string;
  role: string;
  does: string;
  paid: string;
};

/**
 * One reconciled list of the roles a member can be matched with — this is the
 * canonical wording for both the homepage expert cards and the /experts page.
 */
export const EXPERT_ROLES: ExpertRole[] = [
  {
    id: "agent",
    role: "Real estate agent",
    does: "Represents you in the search, the offer, and the negotiation, and coordinates the contract through to closing.",
    paid: "Paid a commission out of the transaction at closing. Home Approach receives no part of it.",
  },
  {
    id: "lender",
    role: "Mortgage lender or loan officer",
    does: "Pre-approves you, quotes rates and fees, and underwrites the loan you actually close with.",
    paid: "Paid by the loan they originate. Home Approach receives nothing from lenders.",
  },
  {
    id: "credit-coach",
    role: "Credit coach",
    does: "Works through what is pulling your score down and the realistic sequence to fix it before you apply.",
    paid: "Usually free to you through nonprofit programs. Home Approach receives nothing from them.",
  },
  {
    id: "inspector",
    role: "Home inspector",
    does: "Inspects the property under contract and reports the condition issues that affect your decision or your negotiation.",
    paid: "Paid directly by you, typically a flat fee at the time of inspection.",
  },
];

export type ExplainerFaq = { id: string; q: string; a: string };

export const EXPLAINER_FAQS: ExplainerFaq[] = [
  {
    id: "do-i-pay",
    q: "Do I pay Home Approach anything?",
    a: "No. Every tool, guide, score check, milestone, and Homie conversation is free. There is no subscription, no card required, and no paid tier hidden behind the free one. You will never be billed by Home Approach.",
  },
  {
    id: "who-funds-cashback",
    q: "Who funds the cashback and Approach Points?",
    a: "Home Approach does, directly out of pocket. The credit-score bonus, milestone payouts, and closing-day cashback are all funded by us — no lender, agent, or sponsor pays you on our behalf, and no reward depends on you choosing a particular company.",
  },
  {
    id: "when-partners-pay",
    q: "Who pays Home Approach?",
    a: "Employers who offer Home Approach as a benefit. No agent, lender, title company, Hero Benefits Alliance, or other provider pays us. Some featured experts may be paid by us for their time creating education; if you work with one, they're paid the normal way through your transaction and Home Approach receives none of it.",
  },
  {
    id: "own-lender",
    q: "Can I use my own lender or agent?",
    a: "Yes. A match is a recommendation, never a requirement. Plenty of members bring their own lender or agent and keep full access to every tool, milestone, and reward. Declining a match costs you nothing and does not reduce your rewards.",
  },
  {
    id: "homie-com",
    q: "Is Homie related to Homie.com?",
    a: "No. Homie is the name of Home Approach's AI guide inside this product. It is not affiliated with Homie.com or with any similarly named brokerage, mortgage company, or app.",
  },
];

/** Copy for the matching-disclosure accordion on /experts. */
export const MATCHING_DISCLOSURE = {
  title: "How matching works, and how we get paid",
  points: [
    {
      id: "matching",
      label: "How you get matched",
      detail:
        "Matches are based on where you are in your journey, your location, and the kind of help you asked for. Featured professionals are labeled as featured.",
    },
    {
      id: "referral-fee",
      label: "No one pays to be here",
      detail:
        "No professional pays Home Approach to be listed, to rank higher, or when you close. Home Approach is paid only by employers.",
    },
    {
      id: "your-choice",
      label: "The choice is always yours",
      detail:
        "You are never required to use anyone we introduce. Bring your own agent, lender, or inspector and you keep every tool and every reward. Home Approach itself is free to use.",
    },
  ],
};

const SITE = "https://www.homeapproach.com";
export const EXPLAINER_URL = `${SITE}/how-we-make-money`;
export const EXPLAINER_TITLE = "Is Home Approach Free? How Home Approach Makes Money";
export const EXPLAINER_DESCRIPTION =
  "Home Approach is free for buyers and paid only by employers. Here is who funds the cashback, why no provider pays us, why you never have to use anyone we suggest, and how Homie differs from Homie.com.";

/** WebPage + BreadcrumbList + FAQPage, baked into server HTML and reused client-side. */
export const EXPLAINER_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: EXPLAINER_TITLE,
      description: EXPLAINER_DESCRIPTION,
      url: EXPLAINER_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: EXPLAINER_TITLE, item: EXPLAINER_URL },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: EXPLAINER_FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
